<script setup lang="ts">
import type { BreadcrumbProps } from '@nuxt/ui';
import type { FoldableBreadcrumbItem } from '#layers/ui/public';
import { computed } from 'vue';
import { omit } from '#layers/base/public';
import { WorkspaceNavigationSelector } from '#layers/spaces/public';
import { BreadcrumbFoldable, usePaneStore } from '#layers/ui/public';
import WorkspaceSwitcher from './WorkspaceSwitcher.vue';

interface Props {
	items?: FoldableBreadcrumbItem[];
	/** Prepend the space-switcher crumb */
	showSpaceSwitcher?: boolean;
	/** Forwarded to `BreadcrumbFoldable`, merged over its internal default. */
	ui?: BreadcrumbProps['ui'];
}

const { items = [], showSpaceSwitcher = false, ui } = defineProps<Props>();

const SWITCHER_SLOTS = ['workspace', 'space'];

const paneStore = usePaneStore();

const breadcrumbItems = computed<FoldableBreadcrumbItem[]>(() => [
	...(paneStore.sidebarSplitPanelCollapsed ? [switcherBreadcrumbItem('workspace')] : []),
	...(showSpaceSwitcher ? [switcherBreadcrumbItem('space')] : []),
	...items,
]);

function switcherBreadcrumbItem(slot: 'workspace' | 'space'): FoldableBreadcrumbItem {
	return { slot, switcher: true, ui: { item: 'px-0' } };
}
</script>

<template>
	<BreadcrumbFoldable :items="breadcrumbItems" :ui>
		<template #workspace="{ folded }">
			<WorkspaceSwitcher placement="breadcrumb" :folded />
		</template>

		<template v-if="showSpaceSwitcher" #space="{ folded }">
			<WorkspaceNavigationSelector :folded />
		</template>

		<template v-for="(_, name) in omit($slots, SWITCHER_SLOTS)" #[name]="slotProps">
			<slot :name v-bind="slotProps" />
		</template>
	</BreadcrumbFoldable>
</template>
